import { Injectable } from '@angular/core';
import { throwError } from 'rxjs/internal/observable/throwError';
import { Router } from '@angular/router';
import { ROUTE_NAMES, PATHS, Constants, ERRORS } from '../../models/constants';
import { CoreSessionService } from './session.service';
import { FormGroup } from '@angular/forms';
import { ApiSchema, ParamsType, SubDomain, ApiParamsSchema, HttpMethods } from '../../models/interfaces';
import { BehaviorSubject } from 'rxjs';
import { CoreHttpService } from '../../utilities/services/http.service';
import messages from './../json/message.json';
import { TranslateService } from '@ngx-translate/core';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { CoreUtilityService } from '../../utilities/services/utility.service';
import { CoreDataShareService } from './data-share.service';
import { MatDialog } from '@angular/material/dialog';
import { Inject } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CoreCommonService {
  // Left Panel sync
  private updateLeftPanelSubject = new BehaviorSubject<any>({});
  updateLeftPanel$ = this.updateLeftPanelSubject.asObservable();
  // Alert bar
  private showAlertBarSubject = new BehaviorSubject<any>({});
  showAlertBar$ = this.showAlertBarSubject.asObservable();

  // Get current Applicants details
  private applicantSubject = new BehaviorSubject<any>({});
  primaryApplicantData$ = this.applicantSubject.asObservable();

  private headerUpdateSubject = new BehaviorSubject<any>({});
  headerUpdate$ = this.headerUpdateSubject.asObservable();

  constructor(
    private router: Router,
    private coreSessionService: CoreSessionService,
    private coreHttpService: CoreHttpService,
    private translate: TranslateService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private coreUtilityService: CoreUtilityService,
    private coreDataShareService: CoreDataShareService,
    public dialog: MatDialog,
    @Inject('ENV_CONFIG') public environment: any
  ) {}

  /**
   * Get api end point from schema
   * @param moduleObj - module object
   * @param apiName - api name
   */
  getApiEndPoint(moduleObj: object, apiName: string): ApiSchema {
    try {
      // if (moduleObj.hasOwnProperty('apis') && moduleObj['apis'].length > 0) {
      const apis: Array<object> = moduleObj['apis'][this.environment.applicationType];
      const filteredList = apis.filter((api) => api['name'] === apiName);
      if (filteredList.length > 0) {
        return filteredList[0] as ApiSchema;
      } else {
        throwError(`api_not_found: ${apiName}`);
      }
      // }
    } catch (error) {
      throwError(`fetch_api_from_schema_failed: ${error}`);
    }
  }

  // TODO: accept multiple params
  /**
   * This method will construct url with params
   * @param baseUrl - base url
   * @param paramKey - param key
   * @param paramValue - param value
   */
  constructUrlWithParams(baseUrl: string, paramKey: string, paramValue: string | number) {
    return `${baseUrl}?${paramKey}=${paramValue}`;
  }

  /**
   * Read file function
   * @param data - file data
   */
  readFile(data: any): Promise<any> {
    const fileReader = new FileReader();
    return new Promise((resolve, reject) => {
      fileReader.onerror = (error) => {
        fileReader.abort();
        reject(throwError(error));
      };
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.readAsDataURL(data);
    });
  }

  /**
   * This method will extract field of specific type
   * @param type - type
   * @param fields - fields
   */
  extractFieldOfType(type: string, fields: Array<object>) {
    const imgField = fields.find((f) => f['fields'][0]['fieldType'] === type);
    return imgField;
  }

  /**
   * This method will extract and construct params
   * @param params - params
   * @param form - form
   */
  extractAndConstructParams(params: Array<ApiParamsSchema>, form?: FormGroup, responseValue?: any, ...scopes: any) {
    try {
      // const params = apiSchema.params;
      if (params.length === 0) {
        return null;
      }
      const newParam = {};
      for (const param of params) {
        let value;
        switch (param.type) {
          case ParamsType.FORM_VALUE:
            value = form.get(param.value).value;
            break;
          case ParamsType.STATIC:
            value = param.value;
            break;
          case ParamsType.SESSION:
            value = this.coreSessionService.getValue(param.value, param.mandatory);
            break;
          case ParamsType.RESPONSE:
            value = responseValue[param.value];
            break;
          case ParamsType.FUNCTION:
            value = this.callFunctionByName(param.value, null, scopes);
            break;
          default:
            break;
        }
        if (!value && param.mandatory) {
          throw new Error(`fetch_param_value_failed`);
        }
        newParam[param.key] = value;
      }
      return newParam;
    } catch (error) {
      throw error;
    }
  }

  /**
   * This method will extract and display default value
   * @param params - params
   * @param form - current form
   */
  async fetchDefaultValue(params: Array<ApiParamsSchema>, form?: FormGroup, ...scopes: any) {
    try {
      if (!params || params?.length === 0) {
        return null;
      }
      let value = null;
      for (const param of params) {
        switch (param.type) {
          case ParamsType.FORM_VALUE:
            value = form.get(param.value).value;
            break;
          case ParamsType.STATIC:
            value = param.value;
            break;
          case ParamsType.SESSION:
            value = this.coreSessionService.getValue(param.value, false);
            break;
          case ParamsType.FUNCTION:
            value = await this.callFunctionByName(param.key, param, scopes);
            break;
          default:
            break;
        }
      }

      return value;
    } catch (error) {
      throw error;
    }
  }

  // TODO: Yet to implement provision for params
  /**
   * This method will call the schema functions by name
   */
  callFunctionByName(fnName: string, param: any, scopes: any) {
    try {
      let counter = 0;
      let value: any;
      for (const scope of scopes) {
        if (scope[fnName]) {
          counter++;
          value = param ? scope[fnName](param) : scope[fnName]();
          // value = scope[fnName]();
          break;
        }
      }
      if (counter === 0) {
        throw new Error(`${ERRORS.FUNCTION_NOT_FOUND}: ${fnName}`);
      }
      return value;
    } catch (error) {
      throwError(`${ERRORS.FUNCTION_CALL_FAILED}: ${error}`);
    }
    return true;
  }

  // TODO: Change thgis. Get tenent and language from url or session.
  /**
   * This method will route user to login
   */
  routeToLogin() {
    try {
      if (this.coreSessionService.tenent) {
        this.router.navigate([this.coreSessionService.tenent, this.coreSessionService.appLanguageCode, ROUTE_NAMES.LOGIN]);
      } else {
        this.router.navigate(['/']);
      }
    } catch (error) {
      console.log(error);
    }
  }

  // TODO: Change thgis. Get tenent and language from url or session.
  /**
   * This method will route user to not-found
   */
  routeToNotFound() {
    try {
      if (this.coreSessionService.tenent) {
        this.router.navigate([this.coreSessionService.tenent, this.coreSessionService.appLanguageCode, ROUTE_NAMES.NOT_FOUND]);
      } else {
        this.router.navigate(['/']);
      }
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will route user to module post logged in to the system using name
   * @param routeName - route name
   */
  routeToPostLoginModule(routeName: string) {
    this.router.navigate([this.coreSessionService.tenent, this.coreSessionService.appLanguageCode, ROUTE_NAMES.BASE_ROUTE, routeName]);
  }

  /**
   * Clears the User Data
   */
  clearStorage() {
    this.coreSessionService.updateLoginStatus(false);
    this.coreSessionService.clearSession();
    this.coreSessionService.clearLocalStorage();
  }

  /**
   * This method will logout the user and route to login
   */
  async logoutAndRouteToLogin() {
    try {
      if (!this.coreSessionService.loginStatus) {
        this.clearStorage();
        this.ngxUiLoaderService.stopAllLoader('loader');
        return this.routeToLogin();
      }
      await this.coreHttpService
        .doPost(Constants.api.logout, {})
        .toPromise()
        .catch((err) => throwError(err))
        .then((val) => val);
      this.clearStorage();
      this.ngxUiLoaderService.stopAllLoader('loader');
      this.routeToLogin();
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will logout the user and route to not-found
   */
  async logoutAndRouteToNotFound() {
    try {
      if (!this.coreSessionService.loginStatus) {
        this.clearStorage();
        this.ngxUiLoaderService.stopAllLoader('loader');
        return this.routeToNotFound();
      }
      await this.coreHttpService
        .doPost(Constants.api.logout, {})
        .toPromise()
        .catch((err) => throwError(err))
        .then((val) => val);
      this.clearStorage();
      this.ngxUiLoaderService.stopAllLoader('loader');
      this.routeToNotFound();
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will set values to session storage
   * @param applicantGroupAlphId - app group id
   * @param applicationAlphId - app id
   * @param visaCategoryId - visa category id
   * @param visaTypeId - visa type id
   * @param routeId - route id
   */
  setSessionValues(applicantGroupAlphId, applicationAlphId, visaCategoryId, visaTypeId, routeId) {
    this.coreSessionService.setApplicantAplhaId(applicationAlphId);
    this.coreSessionService.setApplicantGroupAplhaId(applicantGroupAlphId);
    this.coreSessionService.setCurrApplicantVisaCategory(visaCategoryId);
    this.coreSessionService.setCurrApplicantVisaType(visaTypeId);
    this.coreSessionService.setRouteId(routeId);
    this.coreSessionService.setStorageValue('temp_route_id', routeId);
  }

  /**
   * This method will take care of tasks which need to be completed post login
   * @param token - token
   * @param appSchema - app schema
   * @param status - status
   */
  async doAfterLogin(token, appSchema, status) {
    this.coreSessionService.updateTokenOnRegistration(token);
    await this.coreSessionService.saveApplicationSchemaInBrowser(appSchema);
    this.coreSessionService.updateLoginStatus(status);
  }

  /**
   * This method will extract api as per domain
   * @param apis - api data
   */
  extractApiAsPerDomain(apis: SubDomain): any {
    try {
      return apis[this.environment.applicationType];
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * filters data based on module accessibility
   * @param appSchema - app schema
   */
  moduleAccessibilityFilter(appSchema: object) {
    try {
      const schemaCopy = JSON.parse(JSON.stringify(appSchema));
      const modules: Array<any> = schemaCopy.modules;
      const filteredModules = modules.filter((mod) => {
        const accessibility = mod.accessibility;
        if (accessibility?.domain?.value === Constants.ALL || accessibility?.domain?.value === this.environment.applicationType) {
          return mod;
        }
      });
      schemaCopy.modules = filteredModules;
      return schemaCopy;
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * filters schema based on criteria selected in Eligibility-Criteria module
   * @param appSchema - app schema
   * @param moduleBucketId - bucklet id
   * @param selectedValue - value selected by user
   */
  visaTypeAccessibilityFilter(appSchema: object, moduleBucketId: string, selectedValue?: number) {
    try {
      const schemaCopy = JSON.parse(JSON.stringify(appSchema));
      const modules: Array<any> = schemaCopy.modules;
      let bucket = [];
      if (Constants.MODULE_BUCKET.hasOwnProperty(moduleBucketId)) {
        bucket = Constants.MODULE_BUCKET[moduleBucketId];
      } else {
        throw new Error(ERRORS.BUCKET_NOT_FOUND);
      }
      const filteredModules = modules.filter((mod) => {
        if (bucket.indexOf(mod.logicalName) > -1) {
          return mod;
        }
      });
      schemaCopy.modules = filteredModules;
      return schemaCopy;
    } catch (error) {
      throw error;
    }
  }

  /**
   * filters schema based on criteria selected in Eligibility-Criteria module
   * @param params - params containing mapping of selected value to bucket id
   * @param selectedValue - value selected by user
   */
  getBucketIdFromValue(params: Array<ApiParamsSchema>, selectedValue: any) {
    try {
      const bucket = params.find((param) => Number(param.key) === Number(selectedValue));
      if (bucket) {
        return bucket.value;
      } else {
        throw new Error(ERRORS.NO_BUCKET_PROVIDED);
      }
    } catch (error) {
      throw error;
    }
  }

  // INFO: This function will filter controls based on matching value.
  /**
   * This function will filter control based on selected value.
   * Usually called on value change from eligibility criteria. Function ref in schema
   * @param actualSchema - application schema
   * @param selectedVal - selected value
   */
  filterControlsOnSelectedValues(actualSchema: object, selectedValue: any): object {
    try {
      const schemaCopy = JSON.parse(JSON.stringify(actualSchema));
      const modules: any[] = schemaCopy.modules || [];

      for (const module of modules) {
        const controls: Array<any> = module.controls;
        const filteredControls = controls.filter((ctrl) => {
          for (const field of ctrl.fields) {
            const accessibility = field.accessibility;
            if (accessibility.hasOwnProperty('eligibility') && accessibility.eligibility.hasOwnProperty('value')) {
              const eligibilityValue = accessibility.eligibility.value;
              if (eligibilityValue === Constants.ALL || (Array.isArray(eligibilityValue) && eligibilityValue.indexOf(selectedValue) > -1)) {
                return ctrl;
              }
            }
          }
        });
        module.controls = filteredControls;
      }
      return schemaCopy;
    } catch (error) {
      throwError(`schema_filter_failed: ${error}`);
    }
  }

  /**
   * This method will show alert msgs
   * @param message - msg
   * @param isError - flag to check if msg is error msg or not
   */
  showAlertBar(message, isError) {
    this.showAlertBarSubject.next({ message, isError });
  }

  /**
   * This method will call show alert msgs
   * @param response contain statusCode, status, isError
   */
  onResponsesAlertBar({ statusCode, status, isError }) {
    let errorMessage = this.transalte('commonSections.messages.apiErrorMessages.message1');
    if (
      messages &&
      messages[statusCode] &&
      messages[statusCode][status] &&
      messages[statusCode][status][this.coreSessionService.appLanguageCode]
    ) {
      errorMessage = messages[statusCode][status][this.coreSessionService.appLanguageCode];
      // tslint:disable-next-line: max-line-length
      const linkPath = `${[this.coreSessionService.tenent]}/${[this.coreSessionService.appLanguageCode]}/${messages[statusCode][status]?.link}`;
      const replaceChr = messages[statusCode][status].replaceChr;
      this.showAlertBarSubject.next({
        message: errorMessage,
        isError,
        linkPath,
        replaceChr
      });
    } else {
      if (messages && messages[statusCode] && messages[statusCode][status]) {
        errorMessage = messages[statusCode][status]['en'];
        this.showAlertBarSubject.next({ message: errorMessage, isError });
      } else {
        this.showAlertBarSubject.next({ message: errorMessage, isError });
      }
    }
  }

  /**
   * This method will update the data to left panel
   * @param leftPanel - data
   */
  updateLeftPanel(leftPanel) {
    this.updateLeftPanelSubject.next(leftPanel);
  }

  /**
   * This method will give the primary applicant details from leftpanel api call
   * @param applicant - applicant data
   */
  primaryApplicantDetails(applicant) {
    this.applicantSubject.next(applicant);
  }

  // ------------------------------ Paths ----------------------------------

  /**
   * getter function to fetch image based on mission
   */
  get imagePath() {
    const path = String(PATHS.assets.images).replace('$TENENT', this.coreSessionService.tenent);
    return `/${path}/`;
  }

  /**
   * This method is used for translation
   * @param msg - The msg to be translated
   */
  transalte(msg) {
    let val = '';
    this.translate.get(msg).subscribe((text: string) => {
      val = text;
    });
    return val;
  }

  /**
   * This method will save the current modules data as draft in the system
   */
  async autoSave() {
    try {
      const formData = this.coreDataShareService.formDataWithModuleName;
      const applicationStatus = await this.getApplicationStatus();
      let keys = null;
      if (formData) {
        keys = Object.keys(formData);
      } else {
        return;
      }
      let validFlag = true;
      Object.keys(formData[keys].controls).forEach((ctrl) => {
        if (formData[keys].controls[ctrl].errors && formData[keys].controls[ctrl].errors.hasOwnProperty('required')) {
          formData[keys].controls[ctrl].setErrors(null);
        }

        if (
          (this.coreUtilityService.strictCompare(formData[keys].controls[ctrl].valid, false) &&
            !this.coreUtilityService.strictCompare(formData[keys].controls[ctrl].status, 'DISABLED')) ||
          formData[keys].controls[ctrl].value === ''
        ) {
          validFlag = false;
        }
      });

      const draftData = {
        [keys]: formData[keys].getRawValue()
      };

      if (
        formData &&
        keys &&
        formData.hasOwnProperty(keys[0]) &&
        this.coreUtilityService.strictCompare(applicationStatus?.statusName, keys[0]) &&
        validFlag
      ) {
        const data = await this.coreHttpService
          .doPost(Constants.api.saveAsDraft, draftData)
          .toPromise()
          .catch((err) => {
            throw err;
          })
          .then((val) => val);
        const alphId = this.coreSessionService.applicantAplhaId === 'null' ? null : this.coreSessionService.applicantAplhaId;
        this.updateLeftPanel({ applicationAlphId: alphId });
      }
    } catch (error) {
      console.log(`Error Status: ${error.status}\nMessage: ${error.message}`);
    }
  }

  /**
   * This method will get the application status
   */
  async getApplicationStatus() {
    const appGrpId = this.coreSessionService.applicantGroupAplhaId === 'null' ? null : this.coreSessionService.applicantGroupAplhaId;
    const appId = this.coreSessionService.applicantAplhaId === 'null' ? null : this.coreSessionService.applicantAplhaId;

    if (appGrpId && appId) {
      const params = {
        applicantGroupAlphId: appGrpId,
        applicationAlphId: appId
      };
      const data = await this.coreHttpService
        .doCheck(Constants.api.applicantStatus, HttpMethods.Get, params)
        .toPromise()
        .then((val) => val);
      return data;
    }
  }
}

export interface UrlParams {
  key: string;
  value: string;
}
